import toast, { Toast } from 'react-hot-toast'
import { ubemIoApi } from 'src/redux/services/ubem'

import { Workflow } from '../../consts'
import { fileHandler } from '../../services/utils'
import { setCompletedWorkflow } from '../completion/reducers'
import { setError } from '../error/reducers'
import { clearAllData, loadData, loadMeasureDefinitions } from './reducers'

const workflow = Workflow.upgrades

type ToastAdditional = Partial<
  Pick<Toast, 'className' | 'duration' | 'height' | 'position' | 'icon'>
>

const toastOptions = (
  endpoint?: keyof typeof upgradesApi.endpoints,
  additional?: ToastAdditional,
): Partial<Toast> => ({
  id: endpoint,
  position: 'bottom-center',
  ...additional,
  style: { minWidth: '30%' },
})

export const mutationFixedCacheKeys = {
  uploadBaseUpgradingFile: 'uploadBaseUpgradingFile',
  upgradeAndExport: 'upgradeAndExport',
}

export const upgradesApi = ubemIoApi.injectEndpoints({
  endpoints: (builder) => ({
    // Also called when navigating to upgrades page from project overview page
    uploadBaseUpgradingFile: builder.mutation({
      query: ({ body }) => ({
        url: 'api/upgrader/upload_initial_file',
        method: 'POST',
        body,
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          dispatch(clearAllData())
          if (arg.body.from_cloud) {
            toast.loading(
              'Loading UMI file template library from cloud...',
              toastOptions('uploadBaseUpgradingFile'),
            )
          }
          const { data } = await queryFulfilled
          dispatch(loadData(data))
          if (arg.body.from_cloud) {
            toast.dismiss('uploadBaseUpgradingFile')
          }
        } catch ({ error }) {
          toast.dismiss('uploadBaseUpgradingFile')
          dispatch(
            setError({
              error,
              workflow,
              key: 'uploadBaseUpgradingFile',
            }),
          )
        }
      },
    }),
    getMeasuresLib: builder.query({
      query: () => ({
        url: 'api/upgrader/get_web_measures',
        method: 'GET',
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
        } catch ({ error }) {
          dispatch(setError({ error, workflow, key: 'getMeasuresLib' }))
        }
      },
    }),
    upgradeAndExport: builder.mutation({
      query: (body) => ({
        url: 'api/upgrader/upgrade_and_export',
        method: 'POST',
        body,
        responseHandler: fileHandler,
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          toast.loading('Upgrading template library...', toastOptions('upgradeAndExport'))
          const { data } = await queryFulfilled
          dispatch(setCompletedWorkflow(workflow))
          toast.dismiss('upgradeAndExport')
        } catch ({ error }) {
          toast.dismiss('upgradeAndExport')
          dispatch(
            setError({
              error,
              workflow,
              key: mutationFixedCacheKeys.upgradeAndExport,
            }),
          )
        }
      },
      invalidatesTags: ['Active Project', 'Scenarios'],
    }),
  }),
})

export const { upgradeAndExport, getMeasuresLib, uploadBaseUpgradingFile } = upgradesApi.endpoints
